import axios, { InternalAxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../config/environment";

// Extend the Axios request config to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: Date;
    requestKey: number;
  };
}

// Type for the error handler function
type ErrorHandler = (error: {
  endpoint?: string;
  method?: string;
  error?: object;
  message: string;
  type: string;
  key?: number;
  status?: number;
}) => void;

// Error handler reference for api
let apiErrorHandler: ErrorHandler | null = null;

// Function to set the error handler reference
export const setApiErrorHandler = (handler: ErrorHandler) => {
  apiErrorHandler = handler;
};

export const getApiRequestKey = (): number => {
  return Date.now() + Math.random();
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Enable cookies for authentication
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 60000,
});

// Request interceptor
api.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    // Add timestamp for debugging
    config.metadata = { startTime: new Date(), requestKey: getApiRequestKey() };
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  },
);

// Token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token = null) => {
  console.log("PROCESSING REQUEST QUEUE", error, token);
  failedQueue.forEach((prom) => {
    console.log("QUEUED REQUEST", prom);
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor for error handling, debugging, and automatic token refresh
api.interceptors.response.use(
  (response) => {
    // Log response time
    // const endTime = new Date();
    // const config = response.config as ExtendedAxiosRequestConfig;
    // const duration = config.metadata
    //   ? endTime.getTime() - config.metadata.startTime.getTime()
    //   : 0;

    return response;
  },
  async (error) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig & {
      _retry?: boolean;
    };
    console.error("API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
    });
    if (apiErrorHandler) {
      apiErrorHandler({
        endpoint: error.config?.url,
        method: error.config?.method,
        error: error.response?.data,
        message: error.response?.data.message || "Unknown Error",
        type: "api_error",
        key: error.config?.metadata.requestKey,
        status: error.response?.status,
      });
    }

    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints to avoid infinite loops
      if (
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("auth/login")
      ) {
        console.log("Auth Endpoint Failed, not attempting refresh");
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        console.log("REFRESHING AUTH");
        await api.post("/auth/refresh");
        // Token refreshed successfully
        console.log("REFRESH SUCCESS");
        processQueue(null);
        isRefreshing = false;

        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - redirect to login
        processQueue(refreshError);
        isRefreshing = false;

        // Clear any local auth state and redirect to login
        console.warn("Token refresh failed, redirecting to login");

        // Only redirect if we're not already on the login page
        if (
          !window.location.pathname.includes("/login") &&
          !window.location.pathname.includes("reset-password") &&
          !window.location.pathname.includes("complete-registration")
        ) {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle other error scenarios
    if (error.response?.status === 403) {
      console.warn("Access denied - insufficient permissions");
    } else if (error.response?.status >= 500) {
      console.error("Server error occurred");
    }

    return Promise.reject(error);
  },
);

export default api;
